import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/utils/api-client';
import { PublicListing } from '@/contexts/SearchContext';
import PropertyCard from '@/components/Search/PropertyCard';

export const FeaturedStaysSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [listings, setListings] = useState<PublicListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeatured = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await apiClient.get<{ listings: PublicListing[] }>('/public/listings/featured');
        setListings(response.data.listings || []);
      } catch {
        setListings([]);
      } finally {
        setLoading(false);
      }
    };
    loadFeatured();
  }, []);

  const handleListingClick = (listing: PublicListing): void => {
    navigate(`/listing/${listing.listingId}`);
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h2"
          sx={{ fontWeight: 700, mb: 0.5 }}
        >
          Featured stays
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Popular near you
        </Typography>

        {/* Horizontally scrollable container */}
        <Box
          sx={{
            display: 'flex',
            gap: isMobile ? 2 : 3,
            overflowX: 'auto',
            pb: 2,
            mx: -2,
            px: 2,
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0,0,0,0.15)',
              borderRadius: 3,
            },
          }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Box
                  key={`skeleton-${i}`}
                  sx={{
                    minWidth: { xs: 280, sm: 300, md: 320 },
                    scrollSnapAlign: 'start',
                    flexShrink: 0,
                  }}
                >
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                  <Skeleton width="70%" sx={{ mt: 1.5 }} />
                  <Skeleton width="40%" />
                </Box>
              ))
            : listings.map((listing) => (
                <Box
                  key={listing.listingId}
                  sx={{
                    minWidth: { xs: 280, sm: 300, md: 320 },
                    scrollSnapAlign: 'start',
                    flexShrink: 0,
                  }}
                >
                  <PropertyCard
                    listing={listing}
                    onCardClick={handleListingClick}
                    variant="default"
                  />
                </Box>
              ))}
        </Box>
      </Container>
    </Box>
  );
};
